import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import PaymentService from "../../app/data/payment/paymentService";
import {
  PaymentMethodOption,
  PaymentSessionResponse,
} from "../../app/data/payment/PaymentSession";
import { TokenInfo } from "../../app/data/payment/TokenInfo";
import Amount from "../../ui-components/molecules/amount/amount";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import XGSPopup, { PopupTheme } from "../../ui-components/xgs-popup/xgsPopup";
import XGSIcon from "../../ui-components/icon/xgsIcon";
import XGSIcons from "../../ui-components/icon/xgsIcons";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import Loading from "../../ui-components/loading/loading";
import logo from "../../images/logo-white.png";
import { formatProbillDisplay } from "../shipments/labels-receipts/types";
import "../invoices/invoice-payment/paymentMethodPicker.scss";

const paymentService = PaymentService.getInstance();

type SelectableMethod = "CREDIT_CARD" | "ECHECK";

const METHOD_ICONS: Record<SelectableMethod, typeof XGSIcons.faCreditCard> = {
  CREDIT_CARD: XGSIcons.faCreditCard,
  ECHECK: XGSIcons.faUniversity,
};

export interface PublicPayProbill {
  probillNumber: number;
  openAmountCents: number;
}

interface PublicPayPanelProps {
  email: string;
  onEmailChange: (email: string) => void;
  emailError?: string | null;
  openAmountCents: number;
  probills?: PublicPayProbill[];
  showProbills: boolean;
  showEmail?: boolean;
  disabled?: boolean;
  probillNumber?: number;
  probillNumbers?: number[];
  linkToken?: string;
  onBeforePay?: () => boolean;
}

const centsToDollars = (cents: number): number => cents / 100;

const isAuthorizeNetTokenSuccess = (token: TokenInfo | null | undefined): boolean => {
  if (!token?.token) {
    return false;
  }
  const resultCode = token.messages?.resultCode;
  return resultCode === "Ok" || resultCode === "OK";
};

const authorizeNetTokenError = (token: TokenInfo | null | undefined): string => {
  const messages = token?.messages?.message;
  if (Array.isArray(messages) && messages.length) {
    const text = messages.map((m) => m?.text).filter(Boolean).join("; ");
    if (text) {
      return text;
    }
  }
  return "Authorize.Net payment form failed";
};

const PublicPayPanel: React.FC<PublicPayPanelProps> = ({
  email,
  onEmailChange,
  emailError,
  openAmountCents,
  probills,
  showProbills,
  showEmail = true,
  disabled = false,
  probillNumber,
  probillNumbers,
  linkToken,
  onBeforePay,
}) => {
  const history = useHistory();
  const [methodsOpen, setMethodsOpen] = useState(false);
  const [methods, setMethods] = useState<PaymentMethodOption[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<SelectableMethod | null>(null);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<PaymentSessionResponse | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const processedTransIds = useRef<Set<string>>(new Set());

  const enabledMethods = useMemo(
    () =>
      methods.filter(
        (method) =>
          method.enabled &&
          (method.id === "CREDIT_CARD" || method.id === "ECHECK")
      ),
    [methods]
  );

  const selectedOption = enabledMethods.find((method) => method.id === selectedMethod);
  const tokenInfo = session?.authorizeNetToken as TokenInfo | undefined;

  const invoiceSummary = useMemo(() => {
    const numbers = (probills || [])
      .filter((line) => line.openAmountCents > 0)
      .map((line) => line.probillNumber);
    if (!numbers.length) {
      return null;
    }
    const pills = numbers.slice(0, 3).map((n) => formatProbillDisplay(n));
    return {
      countLabel:
        numbers.length === 1 ? "1 XGS probill number" : `${numbers.length} XGS probill numbers`,
      pills,
      remaining: numbers.length - pills.length,
    };
  }, [probills]);

  const probillKey = (probillNumbers || []).join(",");

  useEffect(() => {
    if (!methodsOpen) {
      return;
    }
    let cancelled = false;
    setLoadingMethods(true);
    setError(null);
    paymentService
      .getPublicPaymentMethods({
        probillNumber,
        probillNumbers: probillKey ? probillKey.split(",").map(Number) : undefined,
        linkToken,
      })
      .then((response) => {
        if (cancelled) {
          return;
        }
        if (!response.ok() || !response.data) {
          const err = response.getError?.();
          setError(typeof err === "string" ? err : "Unable to load payment methods");
          setMethods([]);
          return;
        }
        const available = (response.data.methods || []).filter(
          (method) => method.id !== "ACH"
        );
        setMethods(available);
        const recommended = available.find((method) => method.enabled && method.recommended);
        const first = recommended || available.find((method) => method.enabled);
        setSelectedMethod(first ? (first.id as SelectableMethod) : null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingMethods(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [methodsOpen, probillNumber, probillKey, linkToken]);

  useEffect(() => {
    if (popupOpen && !formSubmitted && tokenInfo?.token) {
      formRef.current?.submit();
      setFormSubmitted(true);
    }
  }, [popupOpen, formSubmitted, tokenInfo]);

  useEffect(() => {
    if (!popupOpen || !session?.paymentId || !session.accessToken) {
      return;
    }
    const paymentId = session.paymentId;
    const accessToken = session.accessToken;
    if (!window.AuthorizeNetIFrame) {
      window.AuthorizeNetIFrame = {};
    }
    window.AuthorizeNetIFrame.onReceiveCommunication = (querystr: string) => {
      const params = parseQueryString(querystr);
      if (params.action === "cancel") {
        setPopupOpen(false);
        setSession(null);
        setFormSubmitted(false);
        return;
      }
      if (params.action !== "transactResponse" || !params.response) {
        return;
      }
      try {
        const response = JSON.parse(params.response);
        const transId = response?.transId ? String(response.transId) : "";
        if (!transId || processedTransIds.current.has(transId)) {
          return;
        }
        processedTransIds.current.add(transId);
        paymentService
          .recordPublicAuthorizeNet(paymentId, accessToken, response)
          .finally(() => {
            history.push(
              `/pay/return?paymentId=${encodeURIComponent(paymentId)}&accessToken=${encodeURIComponent(accessToken)}`
            );
          });
      } catch (err) {
        console.error("Failed to parse payment response", err);
      }
    };
  }, [popupOpen, session, history]);

  const onPay = () => {
    if (onBeforePay && !onBeforePay()) {
      return;
    }
    setError(null);
    setMethodsOpen(true);
  };

  const onContinue = async () => {
    if (!selectedMethod) {
      return;
    }
    setStarting(true);
    setError(null);
    const response = await paymentService.createPublicSession({
      probillNumber: probillNumbers?.length ? undefined : probillNumber,
      probillNumbers,
      linkToken,
      email: email.trim(),
      paymentMethod: selectedMethod,
    });
    setStarting(false);
    if (!response.ok() || !response.data) {
      const err = response.getError?.();
      setError(typeof err === "string" ? err : "Unable to start payment");
      return;
    }
    const data = response.data;
    if (data.redirectUrl) {
      window.location.assign(data.redirectUrl);
      return;
    }
    if (data.authorizeNetToken) {
      const token = data.authorizeNetToken as TokenInfo;
      if (!isAuthorizeNetTokenSuccess(token)) {
        setError(authorizeNetTokenError(token));
        return;
      }
      setSession(data);
      setFormSubmitted(false);
      setMethodsOpen(false);
      setPopupOpen(true);
      return;
    }
    setError("Unexpected payment session response");
  };

  return (
    <>
      {showEmail && (
        <>
          <label className="xgs-public-payment__label" htmlFor="public-pay-email">
            Email address
          </label>
          <input
            id="public-pay-email"
            className="xgs-public-payment__input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
          />
          {emailError && <p className="xgs-public-payment__field-error">{emailError}</p>}
        </>
      )}

      <div className="xgs-public-payment__actions">
        <Button
          theme={ButtonThemes.blue}
          disabled={disabled || openAmountCents <= 0}
          onClick={onPay}
        >
          Pay <Amount amount={centsToDollars(openAmountCents)} />
        </Button>
      </div>

      <XGSPopup
        opened={methodsOpen}
        onClose={() => {
          if (!starting) {
            setMethodsOpen(false);
          }
        }}
        title=""
        className="payment-method-picker"
        size="680px"
        theme={PopupTheme.NEW}
      >
        <div className="xgs-payment-method-picker">
          <div className="xgs-payment-method-picker__header">
            <div className="xgs-payment-method-picker__brand">
              <img className="xgs-payment-method-picker__logo" src={logo} alt="XGS" />
              <h2 className="xgs-payment-method-picker__title">Quick Pay</h2>
            </div>
          </div>
          <div className="xgs-payment-method-picker__body">
            <Loading isLoading={loadingMethods || starting} />
            {error && <XGSErrorMessage>{error}</XGSErrorMessage>}
            <div className="xgs-payment-method-picker__summary">
              <div className="xgs-payment-method-picker__summary-main">
                <div className="xgs-payment-method-picker__context-label">Amount due</div>
                <div className="xgs-payment-method-picker__amount">
                  <Amount amount={centsToDollars(openAmountCents)} />
                </div>
                {showProbills && invoiceSummary && (
                  <div className="xgs-payment-method-picker__invoices">
                    <span>{invoiceSummary.countLabel}</span>
                    <span className="xgs-payment-method-picker__dot">·</span>
                    {invoiceSummary.pills.map((pill) => (
                      <span key={pill} className="xgs-payment-method-picker__invoice-pill">
                        {pill}
                      </span>
                    ))}
                    {invoiceSummary.remaining > 0 && (
                      <span className="xgs-payment-method-picker__invoice-pill">
                        +{invoiceSummary.remaining} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="xgs-payment-method-picker__section-label">Select payment method</div>
            <div className="xgs-payment-method-picker__methods">
              {enabledMethods.map((method) => {
                const id = method.id as SelectableMethod;
                const selected = selectedMethod === id;
                const feeBadge = id === "CREDIT_CARD" && method.surchargeEnabled ? "fee" : "none";
                return (
                  <button
                    key={method.id}
                    type="button"
                    className={`xgs-payment-method-picker__method${
                      selected ? " xgs-payment-method-picker__method--selected" : ""
                    }`}
                    onClick={() => setSelectedMethod(id)}
                  >
                    {selected && (
                      <span className="xgs-payment-method-picker__check" aria-hidden>
                        <XGSIcon icon={XGSIcons.faCheckCircle} />
                      </span>
                    )}
                    <span
                      className={`xgs-payment-method-picker__fee-badge xgs-payment-method-picker__fee-badge--${feeBadge}`}
                    >
                      {feeBadge === "fee" ? "Fees may apply" : "No fees"}
                    </span>
                    <span className="xgs-payment-method-picker__method-icon">
                      <XGSIcon icon={METHOD_ICONS[id]} />
                    </span>
                    <span className="xgs-payment-method-picker__method-label">{method.label}</span>
                    {method.recommended && (
                      <span className="xgs-payment-method-picker__recommended">Recommended</span>
                    )}
                  </button>
                );
              })}
            </div>
            {!!selectedOption?.displayMessage?.trim() && (
              <div className="xgs-payment-method-picker__disclosure">
                <XGSIcon icon={XGSIcons.faInfoCircle} />
                <span>{selectedOption.displayMessage}</span>
              </div>
            )}
            {selectedMethod === "CREDIT_CARD" &&
              selectedOption?.surchargeEnabled &&
              !!selectedOption.surchargeWarning && (
                <div className="xgs-payment-method-picker__disclosure xgs-payment-method-picker__disclosure--warn">
                  <XGSIcon icon={XGSIcons.faInfoCircle} />
                  <span>{selectedOption.surchargeWarning}</span>
                </div>
              )}
          </div>
          <div className="xgs-payment-method-picker__footer">
            <Button theme={ButtonThemes.gray} onClick={() => setMethodsOpen(false)} disabled={starting}>
              Cancel
            </Button>
            <Button
              theme={ButtonThemes.blue}
              onClick={onContinue}
              disabled={!selectedMethod || starting}
              spinner={starting}
            >
              <XGSIcon icon={XGSIcons.faLock} /> Continue
            </Button>
          </div>
        </div>
      </XGSPopup>

      <XGSPopup
        opened={popupOpen}
        onClose={() => {
          setPopupOpen(false);
          setSession(null);
          setFormSubmitted(false);
        }}
        title={
          <>
            <span style={{ marginRight: 10 }}>XGS probill payment:</span>
            <Amount amount={centsToDollars(openAmountCents)} />
          </>
        }
        className="payment-popup"
      >
        <iframe
          title="AuthorizeNetIFrame"
          name="iframe-authorize-net"
          id="iframe-authorize-net"
          src=""
          frameBorder="0"
        />
      </XGSPopup>
      {popupOpen && (
        <form
          ref={formRef}
          method="post"
          action={process.env.REACT_APP_AUTHORIZE_FORM_POST_URL}
          target="iframe-authorize-net"
          style={{ display: "none" }}
        >
          <input type="hidden" name="token" value={tokenInfo?.token ?? ""} />
        </form>
      )}
    </>
  );
};

const parseQueryString = (str: string): Record<string, string> => {
  const vars: Record<string, string> = {};
  str.split("&").forEach((item) => {
    const pair = item.split("=");
    vars[pair[0]] = unescape(pair[1] || "");
  });
  return vars;
};

export default PublicPayPanel;
